import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import ImportReportPanel from "../ImportReport/ImportReportPanel";

/**
 * Modal overlay shown when the user clicks "See details" on an error or
 * warning (partial failure) card.
 *
 * Props:
 *   card       — the ProgressCard object from selectProgressCards
 *   onClose    — () => void
 *
 * Displays:
 *   • Humanized error message (card.error)
 *   • "What to do" action hint (card.actionHint)
 *   • Partial-success summary + per-failed-item accordion (card.failedItems)
 *   • Collapsible "Technical details" section with the raw backend error (card.rawError)
 *
 * Does NOT hold any Redux state — purely presentational.
 * Uses a React Portal so it renders above all other content regardless of stacking context.
 */
export default function ErrorDetailModal({ card, onClose }) {
  const { t } = useTranslation();
  const [rawOpen, setRawOpen] = useState(false);
  // Single-open accordion over the failed items; first row starts open so
  // the modal never opens onto a wall of collapsed rows.
  const [openItemIndex, setOpenItemIndex] = useState(0);

  // Close on Escape key
  const handleKey = useCallback(
    (e) => { if (e.key === "Escape") onClose(); },
    [onClose]
  );
  useEffect(() => {
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  const isDone = card.state === "done";
  // A finished library import with warnings gets the amber header too: not a
  // failure, but something the user needs to read.
  const isWarning = card.state === "warning" || (isDone && (card.warningCount || 0) > 0);
  const failedItems = card.failedItems || [];

  const modal = (
    <div
      className="pn-error-modal__backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={
        isDone
          ? t("progressNotifications.importDetails", "Import details")
          : isWarning
            ? t("progressNotifications.partialDetails", "Partial failure details")
            : t("progressNotifications.errorDetails", "Error details")
      }
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="pn-error-modal">
        {/* Header — amber for partial outcomes, red for total failures */}
        <div className={`pn-error-modal__header${isWarning ? " pn-error-modal__header--warning" : ""}`}>
          <span className="pn-error-modal__header-icon" aria-hidden="true">
            <i
              className={
                isDone && !isWarning
                  ? "fas fa-check-circle"
                  : isWarning
                    ? "fas fa-exclamation-circle"
                    : "fas fa-exclamation-triangle"
              }
            />
          </span>
          <span className="pn-error-modal__title">{card.label}</span>
          <button
            className="pn-error-modal__close"
            onClick={onClose}
            aria-label={t("progressNotifications.close", "Close")}
            type="button"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Body */}
        <div className="pn-error-modal__body">
          {/* Partial success: lead with what worked so the failures below
              read in proportion. */}
          {card.completedCount > 0 && card.failedCount > 0 && (
            <div className="pn-error-modal__message" data-testid="progress-modal-partial-summary">
              <p>
                {t("progressNotifications.partialSummary", {
                  completed: card.completedCount,
                  // Same denominator fallback as the card target: the total
                  // can be absent when the QUEUED frame was dropped.
                  total: card.total || card.completedCount + card.failedCount,
                  defaultValue: `${card.completedCount} of ${card.total || card.completedCount + card.failedCount} succeeded; failed items kept their previous state.`,
                })}
              </p>
            </div>
          )}

          {/* Library CSV import: per-object counters + warnings (+ row-level
              errors on a rejected file) — the same panel the import dialogs
              render, so the card and the dialog never disagree. */}
          {card.importReport && (
            <div className="pn-error-modal__import-report" data-testid="progress-modal-import-report">
              <ImportReportPanel report={card.importReport} />
            </div>
          )}

          {/* Humanized error message */}
          {card.error && (
            <div className="pn-error-modal__message">
              <i className="fas fa-info-circle pn-error-modal__message-icon" aria-hidden="true" />
              <p>{card.error}</p>
            </div>
          )}

          {/* One row per failed item, expanding to its raw backend error. */}
          {failedItems.length > 0 && (
            <div className="pn-error-modal__items">
              {failedItems.map((item, index) => {
                const open = openItemIndex === index;
                return (
                  <div className="pn-error-modal__item" key={`${item.label}-${index}`}>
                    <button
                      type="button"
                      className="pn-error-modal__item-toggle"
                      aria-expanded={open}
                      onClick={() => setOpenItemIndex(open ? -1 : index)}
                      data-testid="progress-modal-failed-item"
                    >
                      <i className="fas fa-times pn-error-modal__item-cross" aria-hidden="true" />
                      <span className="pn-error-modal__item-name">{item.label}</span>
                      {item.message && (
                        <span className="pn-error-modal__item-why">{item.message}</span>
                      )}
                      <i className={`fas fa-chevron-${open ? "up" : "down"}`} aria-hidden="true" />
                    </button>
                    {open && item.raw && (
                      <pre className="pn-error-modal__raw pn-error-modal__item-raw">{item.raw}</pre>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Corrective action hint */}
          {card.actionHint && (
            <div className="pn-error-modal__hint">
              <div className="pn-error-modal__hint-label">
                <i className="fas fa-lightbulb" aria-hidden="true" />
                <span>{t("progressNotifications.whatToDo", "What to do")}</span>
              </div>
              <p>{card.actionHint}</p>
            </div>
          )}

          {/* Collapsible raw error for support */}
          {card.rawError && (
            <div className="pn-error-modal__technical">
              <button
                type="button"
                className="pn-error-modal__technical-toggle"
                onClick={() => setRawOpen((v) => !v)}
                aria-expanded={rawOpen}
              >
                <i className={`fas fa-chevron-${rawOpen ? "up" : "down"}`} aria-hidden="true" />
                <span>{t("progressNotifications.technicalDetails", "Technical details")}</span>
              </button>
              {rawOpen && (
                <pre className="pn-error-modal__raw">{card.rawError}</pre>
              )}
            </div>
          )}

          <p className="pn-error-modal__support">
            {t("progressNotifications.supportHelpPrefix", "Need help?")}
            {" "}
            <a
              href="https://offolio.freshdesk.com/support/home"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("progressNotifications.supportHelpLink", "Read documentation or contact support")}
            </a>
            .
          </p>
        </div>

        {/* Footer */}
        <div className="pn-error-modal__footer">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
            {t("progressNotifications.close", "Close")}
          </button>
        </div>
      </div>
    </div>
  );

  // Render into document.body so z-index stacking is never an issue
  return ReactDOM.createPortal(modal, document.body);
}
